import React from 'react';
import PropTypes from 'prop-types';
import combineReducers from '@americanexpress/vitruvius/immutable';

import { fromJS } from 'immutable';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from '@americanexpress/one-app-router';
import { loadLanguagePack } from '@americanexpress/one-app-ducks';
import { OneFetchyeProvider, OneCache } from 'fetchye-one-app';
import { composeModules, RenderModule } from 'holocron';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import { Helmet } from 'react-helmet';
import SharedScript from '@americanexpress/shared-scripts';

import childRoutes from '../childRoutes';
import { useRootSignalListeners } from '../signals';

const AxpInternationalShopRoot = ({
  children, isAuth, languageData, locale,
}) => {
  useRootSignalListeners();

  return (
    <>
      <Helmet
        meta={[
          { charset: 'utf-8' },
          { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        ]}
        link={[
          {
            rel: 'icon',
            href: 'https://www.americanexpress.com/favicon.ico',
          },
        ]}
        htmlAttributes={{ lang: locale }}
      />
      <RenderModule moduleName="axp-one-seo" props={{ fallbacks: languageData.seoFallbacks }} />
      <OneFetchyeProvider>
        {isAuth && (
          <SharedScript name="one-identity-session" version="^1.8.1" windowCheck="AmexSession" />
        )}
        {children}
      </OneFetchyeProvider>
    </>
  );
};

export const TestableAxpInternationalShopRoot = AxpInternationalShopRoot;

AxpInternationalShopRoot.propTypes = {
  children: PropTypes.shape({}),
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  locale: PropTypes.string,
  isAuth: PropTypes.bool,
  languageData: PropTypes.shape({
    applicationName: PropTypes.string,
    seoFallbacks: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      openGraph: PropTypes.shape({
        type: PropTypes.string,
        title: PropTypes.string,
        description: PropTypes.string,
      }),
    }),
  }),
};

// Read about childRoutes:
// https://github.com/americanexpress/one-app/blob/main/docs/api/modules/Routing.md#childroutes
AxpInternationalShopRoot.childRoutes = childRoutes;

// Read about appConfig:
// https://github.com/americanexpress/one-app/blob/main/docs/api/modules/App-Configuration.md
/* istanbul ignore next */
if (!global.BROWSER) {
  // eslint-disable-next-line global-require -- require needs to be inside browser check
  AxpInternationalShopRoot.appConfig = require('../config').default;
}

const getCurrentRoute = (ownProps) => {
  // the current route is the last route
  const routes = ownProps?.routes || [];
  return routes[routes.length - 1] || {};
};

export const loadModuleData = async ({ store: { dispatch }, ownProps }) => {
  const currentRoute = getCurrentRoute(ownProps);

  const modulesToCompose = [
    dispatch(composeModules([{ name: 'axp-one-seo' }])),
    dispatch(
      loadLanguagePack('axp-international-shop-root', {
        fallbackLocale: 'en-CA',
      })
    ),
    dispatch(composeModules([{ name: 'axp-error-message' }])),
    dispatch(composeModules([{ name: 'axp-global-header' }])),
    dispatch(composeModules([{ name: 'axp-footer' }])),
  ];

  if (currentRoute.moduleToCompose) {
    modulesToCompose.push(
      dispatch(composeModules([{ name: currentRoute.moduleToCompose, props: ownProps }]))
    );
  }

  await Promise.allSettled(modulesToCompose);
};

export const mapStateToProps = (state) => ({
  isAuth: state.getIn(['modules', 'axp-global-header', 'authenticated']),
  oneLoginUrl: state.getIn(['config', 'oneLoginUrl']),
});

export const reqReducer = (state = fromJS({})) => state;
reqReducer.buildInitialState = ({ req } = {}) => req;

AxpInternationalShopRoot.holocron = {
  name: 'axp-international-shop-root',
  loadModuleData,
  reducer: combineReducers({
    fetchye: OneCache().reducer,
    reqReducer,
  }),
};

const hocChain = compose(
  oneAppModuleWrapper('axp-international-shop-root'),
  connect(mapStateToProps, null),
  withRouter
);

export default hocChain(AxpInternationalShopRoot);
